import { unwrap } from "@sagot/api-client";
import { SSO_DOMAINS_MAX, SSO_ERROR_QUERY, SSO_KINDS, SSO_TEST_QUERY, type StaffSsoKind } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, CopyButton, EmptyState, Field, Icon, Input, ListEditor, Select, Switch, toast, type ListEditorLabels, type ListEntryResult } from "@sagot/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ChangeEvent, type SyntheticEvent } from "react";
import { useSearchParams } from "react-router";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { UpgradeCard } from "../../shared";
import { CardActions, CardSkeleton, PanelError, useCanManage } from "./panel-kit";
import { asReason, bodyOf, canEnforce, draftOf, isPublicMailDomain, metadataTooLarge, parseSsoDomain, problemsOf, type SsoDraft, type SsoProblem, type StaffSso } from "./sso-model";

const K = "settings.security.sso";
const SEC = "settings.security";

function useSso() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: ["ws", wsId, "staff-sso"] as const, queryFn: () => unwrap(api.GET("/v1/staff-sso")) });
}

function ServiceProviderDetails({ sso }: { sso: StaffSso }) {
  const { t } = useTranslation();
  const rows: [string, string][] =
    sso.kind === "oidc"
      ? [[t(`${K}.spRedirect`), sso.service_provider.redirect_uri]]
      : [
          [t(`${K}.spEntityId`), sso.service_provider.entity_id],
          [t(`${K}.spAcs`), sso.service_provider.acs_url],
          [t(`${K}.spMetadata`), sso.service_provider.metadata_url],
        ];
  return (
    <div className="space-y-2 px-5 py-4" data-testid="sso-sp-details">
      <h3 className="text-sm font-semibold">{t(`${K}.spTitle`)}</h3>
      <ul className="space-y-2">
        {rows.map(([label, value]) => (
          <li key={label} className="flex items-center gap-2 rounded-lg border border-line bg-sunken px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="text-xs text-fg-muted">{label}</div>
              <div className="break-all font-mono text-xs">{value}</div>
            </div>
            <CopyButton value={value} label={`${t(`${K}.copy`)} ${label}`} copiedLabel={t(`${K}.copied`)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProviderFields({ draft, sso, errors, disabled, patch }: { draft: SsoDraft; sso: StaffSso | undefined; errors: SsoProblem[]; disabled: boolean; patch: (next: Partial<SsoDraft>) => void }) {
  const { t } = useTranslation();
  const file = useRef<HTMLInputElement>(null);
  const [oversize, setOversize] = useState(false);
  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    event.target.value = "";
    if (!chosen) return;
    const tooLarge = metadataTooLarge(chosen.size);
    setOversize(tooLarge);
    if (!tooLarge) patch({ metadataXml: await chosen.text(), metadataName: chosen.name });
  };
  if (draft.kind === "saml") {
    const stored = sso?.kind === "saml" ? sso.saml : null;
    return (
      <div className="space-y-3 px-5 py-4">
        <div className="text-sm font-semibold">{t(`${K}.metadata`)}</div>
        <p className="text-xs text-fg-muted">{t(`${K}.metadataHint`)}</p>
        <input ref={file} type="file" accept=".xml,text/xml,application/xml" className="sr-only" tabIndex={-1} aria-hidden="true" data-testid="sso-metadata-input" onChange={(event) => void upload(event)} />
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="secondary" disabled={disabled} onClick={() => file.current?.click()}>
            <Icon name="upload" />
            {t(`${K}.metadataUpload`)}
          </Button>
          {draft.metadataName ? <span className="text-sm text-fg-muted">{t(`${K}.metadataLoaded`, { name: draft.metadataName })}</span> : null}
        </div>
        {oversize ? <p role="alert" className="text-xs font-semibold text-danger">{t(`${K}.metadataTooLarge`)}</p> : null}
        {errors.includes("metadata") ? <p role="alert" className="text-xs font-semibold text-danger">{t(`${K}.metadataHint`)}</p> : null}
        {stored ? (
          <div className="rounded-lg border border-line bg-sunken px-3 py-2 text-xs" data-testid="sso-saml-stored">
            <div className="font-mono break-all">{stored.idp_entity_id}</div>
            <div className="mt-1 text-fg-muted">{t(`${K}.certificates`)}</div>
            <ul className="mt-1 space-y-0.5">
              {stored.certificates.map((c) => (
                <li key={c.fingerprint_sha256} className="font-mono break-all">
                  {c.fingerprint_sha256.slice(0, 23)}… <span className="font-sans text-fg-muted">{t(`${K}.certExpires`, { date: new Date(c.not_after).toLocaleDateString() })}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }
  const savedSecret = sso?.kind === "oidc" && sso.oidc?.has_secret;
  return (
    <div className="grid grid-cols-1 gap-4 px-5 py-4 md:grid-cols-2">
      <Field label={t(`${K}.issuer`)} hint={t(`${K}.issuerHint`)} error={errors.includes("issuer") ? t(`${K}.issuerHint`) : undefined} className="md:col-span-2">
        <Input value={draft.issuer} disabled={disabled} inputMode="url" autoComplete="off" onChange={(event) => { patch({ issuer: event.target.value }); }} />
      </Field>
      <Field label={t(`${K}.clientId`)} error={errors.includes("clientId") ? t(`${K}.clientId`) : undefined}>
        <Input value={draft.clientId} disabled={disabled} autoComplete="off" onChange={(event) => { patch({ clientId: event.target.value }); }} />
      </Field>
      <Field label={t(`${K}.clientSecret`)} hint={savedSecret ? t(`${K}.clientSecretSaved`) : undefined} error={errors.includes("clientSecret") ? t(`${K}.clientSecret`) : undefined}>
        <Input type="password" value={draft.clientSecret} disabled={disabled} autoComplete="new-password" onChange={(event) => { patch({ clientSecret: event.target.value }); }} />
      </Field>
    </div>
  );
}

function DomainList({ domains, onChange, disabled }: { domains: readonly string[]; onChange: (next: string[]) => void; disabled: boolean }) {
  const { t } = useTranslation();
  const labels: ListEditorLabels = {
    add: t(`${K}.domainAdd`),
    placeholder: t(`${K}.domainPlaceholder`),
    empty: t(`${SEC}.domainsEmpty`),
    count: (count, max) => t(`${SEC}.domainsCount`, { count, max }),
    remove: (domain) => t(`${SEC}.domainsRemove`, { domain }),
    duplicate: (domain) => t(`${SEC}.domainsDuplicate`, { domain }),
    full: t(`${SEC}.domainsFull`, { max: SSO_DOMAINS_MAX }),
    added: (domain) => t(`${SEC}.domainsAdded`, { domain }),
    removed: (domain) => t(`${SEC}.domainsRemoved`, { domain }),
  };
  const parse = (raw: string): ListEntryResult => {
    const value = parseSsoDomain(raw);
    if (!value) return { ok: false, error: t(`${K}.domainInvalid`) };
    return isPublicMailDomain(value) ? { ok: false, error: t(`${K}.domainPublic`, { domain: value }) } : { ok: true, value };
  };
  return <ListEditor label={t(`${K}.domains`)} hint={t(`${K}.domainsHint`, { count: SSO_DOMAINS_MAX })} items={domains} onChange={onChange} parse={parse} max={SSO_DOMAINS_MAX} labels={labels} icon="mail" disabled={disabled} />;
}

function Enforcement({ draft, sso, disabled, patch }: { draft: SsoDraft; sso: StaffSso | undefined; disabled: boolean; patch: (next: Partial<SsoDraft>) => void }) {
  const { t } = useTranslation();
  const allowed = canEnforce(sso);
  return (
    <div className="space-y-3 px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div id="sso-enforce-label" className="text-sm font-semibold">{t(`${K}.enforceTitle`)}</div>
          <div className="text-xs text-fg-muted">{allowed ? t(`${K}.enforceBody`) : t(`${K}.enforceNeedsTest`)}</div>
        </div>
        <Switch checked={draft.enforce} disabled={disabled || (!allowed && !draft.enforce)} onCheckedChange={(checked) => { patch({ enforce: checked }); }} aria-labelledby="sso-enforce-label" />
      </div>
      {draft.enforce && sso ? (
        <Field label={t(`${K}.breakGlass`)} hint={t(`${K}.breakGlassHint`)}>
          <Select value={draft.breakGlassUserId} disabled={disabled} onChange={(event) => { patch({ breakGlassUserId: event.target.value }); }}>
            {sso.owners.map((owner) => (
              <option key={owner.id} value={owner.id}>{`${owner.name} (${owner.email})`}</option>
            ))}
          </Select>
        </Field>
      ) : null}
    </div>
  );
}

function SsoForm({ sso, onSaved }: { sso: StaffSso | undefined; onSaved: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  const manage = useCanManage("settings.manage");
  const [draft, setDraft] = useState<SsoDraft>(() => draftOf(sso));
  const [errors, setErrors] = useState<SsoProblem[]>([]);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const patch = (next: Partial<SsoDraft>) => { setDraft((d) => ({ ...d, ...next })); };
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["ws", wsId, "staff-sso"] });
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/staff-sso", { body: bodyOf(draft) })),
    onSuccess: async () => { toast.success(t(`${K}.saved`)); await refresh(); onSaved(); },
    onError: (error) => { toast.error(message(error)); },
  });
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/staff-sso")),
    onSuccess: async () => { toast.success(t(`${K}.removed`)); setConfirmRemove(false); await refresh(); onSaved(); },
    onError: (error) => { toast.error(message(error)); },
  });
  const test = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/staff-sso/test")),
    onSuccess: (started) => { window.location.assign(started.start_url); },
    onError: (error) => { toast.error(message(error)); },
  });
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const found = problemsOf(draft, sso);
    setErrors(found);
    if (found.length === 0) save.mutate();
  };
  const locked = !manage.allowed;
  const configured = Boolean(sso?.configured);
  return (
    <form className="card" onSubmit={submit} noValidate aria-labelledby="sso-title" data-testid="sso-form">
      <div className="card-header">
        <div>
          <h2 id="sso-title" className="card-title flex flex-wrap items-center gap-2">
            {t(`${K}.title`)}
            {sso?.enforce ? <Badge tone="success">{t(`${K}.enforceTitle`)}</Badge> : null}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t(`${K}.body`)}</p>
        </div>
      </div>
      <fieldset className="divide-y divide-line" disabled={locked}>
        <div className="px-5 py-4">
          <Field label={t(`${K}.protocol`)}>
            <Select value={draft.kind} disabled={configured} onChange={(event) => { patch({ kind: event.target.value as StaffSsoKind }); }}>
              {SSO_KINDS.map((kind) => (
                <option key={kind} value={kind}>{t(`${K}.${kind}`)}</option>
              ))}
            </Select>
          </Field>
        </div>
        {sso ? <ServiceProviderDetails sso={{ ...sso, kind: draft.kind }} /> : null}
        <ProviderFields draft={draft} sso={sso} errors={errors} disabled={locked} patch={patch} />
        <div className="px-5 py-4">
          <DomainList domains={draft.domains} onChange={(domains) => { patch({ domains }); }} disabled={locked} />
          {errors.includes("domains") ? <p role="alert" className="mt-1.5 text-xs font-semibold text-danger">{t(`${K}.domainsHint`, { count: SSO_DOMAINS_MAX })}</p> : null}
        </div>
        <Enforcement draft={draft} sso={sso} disabled={locked} patch={patch} />
        {configured ? (
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <div>
              <div className="text-sm font-semibold">{t(`${K}.test`)}</div>
              <div className="text-xs text-fg-muted" data-testid="sso-tested">
                {sso?.last_tested_at ? t(`${K}.tested`, { date: new Date(sso.last_tested_at).toLocaleString() }) : t(`${K}.neverTested`)}
              </div>
              <div className="text-xs text-fg-muted">{t(`${K}.testHint`)}</div>
            </div>
            <Button type="button" variant="secondary" loading={test.isPending} disabled={locked} title={manage.reason} onClick={() => { test.mutate(); }}>
              <Icon name="log-in" />
              {t(`${K}.test`)}
            </Button>
          </div>
        ) : null}
      </fieldset>
      <CardActions>
        {configured ? (
          confirmRemove ? (
            <>
              <span className="mr-auto self-center text-xs text-fg-muted" role="alert">{t(`${K}.removeBody`)}</span>
              <Button type="button" variant="ghost" onClick={() => { setConfirmRemove(false); }}>{t("common.cancel")}</Button>
              <Button type="button" variant="danger" loading={remove.isPending} onClick={() => { remove.mutate(); }}>{t(`${K}.remove`)}</Button>
            </>
          ) : (
            <Button type="button" variant="ghost" className="mr-auto" disabled={locked} title={manage.reason} onClick={() => { setConfirmRemove(true); }}>
              <Icon name="trash" />
              {t(`${K}.remove`)}
            </Button>
          )
        ) : null}
        <Button type="submit" disabled={locked} loading={save.isPending} title={manage.reason}>{t(`${K}.save`)}</Button>
      </CardActions>
    </form>
  );
}

/** Banner for the result of a test sign-in (the API redirects back with `sso_test` / `sso_error`). */
function TestResult() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const result = params.get(SSO_TEST_QUERY);
  const reason = asReason(params.get(SSO_ERROR_QUERY));
  useEffect(() => {
    if (!result) return;
    const next = new URLSearchParams(params);
    next.delete(SSO_TEST_QUERY);
    next.delete(SSO_ERROR_QUERY);
    const timer = window.setTimeout(() => { setParams(next, { replace: true }); }, 0);
    return () => { window.clearTimeout(timer); };
  }, [result, params, setParams]);
  const [shown] = useState(result);
  if (!shown) return null;
  const ok = shown === "ok";
  return (
    <div role={ok ? "status" : "alert"} className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${ok ? "border-success bg-success-soft text-success-ink" : "border-danger bg-danger-soft text-danger-ink"}`} data-testid="sso-test-result">
      <Icon name={ok ? "check-circle" : "alert-circle"} className="mt-0.5 size-4 shrink-0" />
      {ok ? t(`${K}.testOk`) : t(`${K}.testFailed`, { reason: t(`${K}.reasons.${reason}`) })}
    </div>
  );
}

/** Settings → Security "Single sign-on": SAML or OpenID Connect per workspace; Pro and above. */
export function SsoCard() {
  const { t } = useTranslation();
  const sso = useSso();
  const [editing, setEditing] = useState(false);
  if (sso.isPending) return <CardSkeleton rows={3} />;
  if (sso.isError) return <PanelError error={sso.error} onRetry={() => void sso.refetch()} title={t(`${K}.loadError`)} />;
  const data = sso.data;
  if (!data.entitled) return <UpgradeCard title={t(`${K}.lockedTitle`)} body={t(`${K}.lockedBody`)} icon="key" />;
  return (
    <div className="space-y-4">
      <TestResult />
      {!data.configured && !editing ? (
        <section className="card" aria-labelledby="sso-title">
          <h2 id="sso-title" className="sr-only">{t(`${K}.title`)}</h2>
          <EmptyState icon="key" title={t(`${K}.emptyTitle`)} description={t(`${K}.emptyBody`)} action={<Button onClick={() => { setEditing(true); }}>{t(`${K}.connect`)}</Button>} />
        </section>
      ) : (
        <SsoForm key={`${String(data.updated_at)}-${String(data.last_tested_at)}`} sso={data} onSaved={() => { setEditing(false); }} />
      )}
    </div>
  );
}
